import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS } from '../../../../../../shared/featureFlags/flagNames';
import { isFreeTier, PREMIUM_COMPLIANCE_PLAN_NAME } from '../../../../../../shared/subscriptions';
import { window } from '../../../../../globals';
import { useIsFeatureEnabled } from '../../../../../helpers/features';
import { useToggle } from '../../../../../hooks';
import * as reportGeneration from '../../../../../redux/reportGeneration';
import { BUTTON_SIZE, BUTTON_TYPE, Icon, icons, NavigationButton } from '../../../../generic';
import { TooltipButton } from '../../../../generic/Button';
import Flex from '../../../../generic/Flex';
import PaidFeatureGate from '../../../../generic/Gates/PaidFeatureGate';
import useReportContainer from '../context/useReportContainer';
import { getDownloadURL } from '../projectLevel/utils';
import { ReportCategories } from '../utils';
import type { Revision } from '../../../../../../shared/model-typings';
import type { ProjectContext, ReportCategory, ReportExclusionOptions } from '../../shared/types';
import type {
  DependencyMetadataFilterOption,
  FormatName,
  IncludeFilterOption,
  IncludeFixPlanFilterOption,
} from '../shared/types';

import EmailDialog from './EmailDialog';
import PublicLinkButton from './PublicLinkButton';

// Number of dependencies above which a project's licensing report will
// use the "async email" workflow instead of allowing a direct download.
export const EMAIL_DEPENDENCY_THRESHOLD = 500;

type Props = {
  isDisabled?: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  fixPlanOptions: IncludeFixPlanFilterOption[];
  revision?: Revision;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
};

const ProjectActionButtons: VoidFunctionComponent<Props> = ({
  format,
  reportType,
  includeOptions,
  dependencyMetadataOptions,
  fixPlanOptions,
  isDisabled,
  revision,
  authorName,
  authorEmail,
  supplier,
  excludeOptions,
}: Props) => {
  const { useReportsV2Toggle } = useReportContainer();

  const isLicensingReport = reportType === ReportCategories.Licensing;
  const isSbomReport = reportType === ReportCategories.SBOM;
  const isFixPlanReport = reportType === ReportCategories.RemediationGuidance;

  const isPaidFeatureGateRestricted = isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const reportContext = useSelector(reportGeneration.selectors.getReportContext);
  const [isToggled, onToggle] = useToggle();

  const shouldAlwaysDownloadReports =
    useIsFeatureEnabled(ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS);

  // Use the revision's dep count to determine if the report should be emailed (instead of downloading).
  const dependencyCount = revision?.dependency_count ?? 0;
  const areDepsOverThreshold = dependencyCount > EMAIL_DEPENDENCY_THRESHOLD;
  // Other case is that Licensing PDF reports are always emailed
  const isLicensingReportPDF = isLicensingReport && (format === 'PDF');
  const shouldEmailReport = !isFixPlanReport && (isLicensingReportPDF
    || (areDepsOverThreshold && !shouldAlwaysDownloadReports));

  // Large projects will sometimes fail to generate PDF reports due to the size of the report.
  // This is most likely to occur when the project has a large number of dependencies and the license or copyright
  // text is included for each dependency. We use this value to determine if we should show a tooltip to the user
  // letting them know that the report may be slow, or fail to generate.
  const riskyPDFOptions = new Set(['FullTextLicense', 'CustomTextLicense', 'Copyrights']);
  const isRiskyPDFSize =
    dependencyCount > EMAIL_DEPENDENCY_THRESHOLD &&
    isLicensingReportPDF &&
    dependencyMetadataOptions.some((option) => riskyPDFOptions.has(option));

  const tooltip = isRiskyPDFSize
    ? 'Due to the project\'s size, generating this PDF is likely to fail. We recommend selecting HTML and converting to a PDF after generation.'
    : 'Due to the project\'s size, this report will be delivered via email when ready.';

  // Show the PublicLinkButton if showing download button for  licensing and sbom reports.
  const shouldShowPublicLinkButton = (isLicensingReport || isSbomReport) && !shouldEmailReport;

  return (
    <Flex gap="4px">
      {isToggled && (
      <EmailDialog
        onClose={onToggle}
        format={format}
        reportType={reportType}
        includeOptions={includeOptions}
        dependencyMetadataOptions={dependencyMetadataOptions}
        authorName={authorName}
        authorEmail={authorEmail}
        supplier={supplier}
        excludeOptions={excludeOptions}
      />
      )}
      {shouldShowPublicLinkButton && (
      <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}>
        <PublicLinkButton
          isDisabled={isDisabled}
          format={format}
          reportType={reportType}
          includeOptions={includeOptions}
          dependencyMetadataOptions={dependencyMetadataOptions}
          authorName={authorName}
          authorEmail={authorEmail}
          supplier={supplier}
          excludeOptions={excludeOptions}
        />
      </PaidFeatureGate>
      )}
      {shouldEmailReport ? (
        <TooltipButton
          id="button-email-report"
          tooltip={tooltip}
          onClick={onToggle}
          showTooltip={!isToggled}
          size={BUTTON_SIZE.LARGE}
          type={BUTTON_TYPE.SECONDARY}
          isDisabled={isDisabled}
        >
          <Icon icon={icons.envelopeRegular} /> Email
        </TooltipButton>
      ) : (
        <NavigationButton
          isDisabled={isDisabled}
          openInNewTab
          size={BUTTON_SIZE.LARGE}
          to={
            getDownloadURL({
              format,
              projectRevisionLocator: (reportContext as ProjectContext).revision,
              reportType,
              includeOptions,
              dependencyMetadataOptions,
              fixPlanOptions,
              authorName,
              authorEmail,
              supplier,
              excludeOptions,
              useReportsV2: useReportsV2Toggle,
            }) || ''
          }
        >
          <Icon icon={icons.downloadRegular} /> Download
        </NavigationButton>
      )}
    </Flex>);
};

export default ProjectActionButtons;
